import { Card, CardContent } from "../../../Components/UI/card"
import { Button } from "../../../Components/UI/button"
import { UserCheck, X, Check, Ban, Building2 } from "lucide-react"
import { useMemo, useState, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { toast } from "sonner"
import { useDesktop } from "../../../Components/Common/IsDesktop"
import CompleteThreadDialog from "../../../Components/Common/CompleteThreadDialog"
import CancelThreadDialog from "../../../Components/Common/CancelThreadDialog"
import MastersDialog from '../../Settings/Masters';
import { checkIfInqOnlyMode, getThreadTypesArr, getActiveThreadTypesArr } from "../../../Store/Reducers"
import { currentUserHasPermission, getThreadModeTitle, isPendingTasks } from "../../../Utils/Utilities"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "../../../Components/UI/alert-dialog"
import apiService from "../../../Services/APIService"
import { manageThreadAPI } from "../ThreadsAPI"
import { setGlobalLoading } from "../../../Store/Actions"

const ThrDetailsActions = ({
  threadData,
  onReassign,
  onCloseThread,
  onUpdateStatus,
  onConvertInquiry,
  onMarkFake,
  canShowReassignButton = true,
  compact = false,
  onCloseModal,
  isOrder = false,
  onCustomStatusUpdate,
  statusText = '',
}) => {
  const isDesktop = useDesktop();

  // Dialog states
  const [dialogStates, setDialogStates] = useState({
    confirmDialog: false,
    rejectionDialog: false,
  });

  const [mastersDialog, setMastersDialog] = useState({
    open: false,
    masterType: '',
    directAdd: false,
    openEntry: false,
    showThreadTypeSelection: false,
    currentThreadTypeID: null,
    prefilledName: ''
  });

  const [attachAlertDialogOpen, setAttachAlertDialogOpen] = useState(false);
  const [completedThreadData, setCompletedThreadData] = useState(null);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  // Permissions
  const thrActions = currentUserHasPermission('ThrActions');
  const thrViewTypeID = currentUserHasPermission('ThrViewTypeID');

  const canReassign = thrViewTypeID === 1 && canShowReassignButton;
  const canPerformActions = thrActions === 1 || thrActions >= 2;

  // Thread type data
  const threadTypes = useSelector(getThreadTypesArr);
  const activeThreadTypesArr = useSelector(getActiveThreadTypesArr);
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);

  const isInquiry = threadTypes.find(tt => tt.TID === threadData?.typeID)?.SnSng === "Inquiry";

  // Disqualification & Rejection reasons
  const masterData = useSelector((state) => state.masterData);
  const allDisqlRsns = useSelector((state) => state.masterData.disqlReasons) || [];

  const disqlRsns = useMemo(() => {
    if (!allDisqlRsns.length || !threadData?.typeID) return [];
    const current = allDisqlRsns.find(item => item.TID === threadData.typeID);
    if (!current?.Rsns) return [];
    try {
      return JSON.parse(current.Rsns);
    } catch (e) {
      console.error("Error parsing disql reasons", e);
      return [];
    }
  }, [allDisqlRsns, threadData?.typeID]);

  const rejectionReasonOptions = useMemo(() => {
    if (!masterData?.rejectionReasons || !threadData?.typeID) return [];
    const current = masterData.rejectionReasons.find(item => item.TID === threadData.typeID);
    if (!current?.Rsns) return [];
    try {
      const reasons = JSON.parse(current.Rsns);
      return reasons.map(r => ({ value: r.RID, label: r.R }));
    } catch (e) {
      console.error("Error parsing rejection reasons", e);
      return [];
    }
  }, [masterData?.rejectionReasons, threadData?.typeID]);

  const getBtnText = () => {
    if (isInquiry) {
      return activeThreadTypesArr.length > 2 ? 'Close Inquiry' : 'Convert to Sale';
    }
    if ([2, 5].includes(threadData?.typeID)) return 'Complete Sale';
    if ([3, 6].includes(threadData?.typeID)) return 'Close Ticket';
    return 'Done';
  };

  const handleCompleteClick = () => {
    if (isInquiry) {
      if (isPendingTasks(threadData?.threadID, 'Cannot convert inquiry.', 'Please complete tasks before converting this inquiry.')) return;
      if (onConvertInquiry) {
        onConvertInquiry(null, 'convert');
      }
    } else {
      setDialogStates(prev => ({ ...prev, confirmDialog: true }));
    }
  };

  const handleCompleteThread = async ({ shareByEmail, shareByWhatsApp, takeFeedback, completionNote }) => {
    setDialogStates(prev => ({ ...prev, confirmDialog: false }));
    const isSale = [2, 5].includes(Number(threadData?.typeID));
    if (onUpdateStatus) {
      await onUpdateStatus(5, true, shareByEmail, takeFeedback, completionNote, shareByWhatsApp);
    }
    if (isSale) {
      setCompletedThreadData(threadData);
      setAttachAlertDialogOpen(true);
    }
  };

  const handleAttachDocumentClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSkipAttachment = () => {
    setAttachAlertDialogOpen(false);
    setCompletedThreadData(null);
    if (onCloseModal) {
      onCloseModal();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (file.size > 1024 * 1024) {
      toast.error("File size must be up to 1 MB only.");
      e.target.value = '';
      setAttachAlertDialogOpen(true);
      return;
    }

    try {
      dispatch(setGlobalLoading(true));
      const uploadResult = await apiService.uploadFile(file, "Threads", "both");

      if (uploadResult?.error) {
        toast.error(uploadResult?.message || "Failed to upload file");
        setAttachAlertDialogOpen(true);
        return;
      }

      const uploadedFileName = uploadResult?.data?.relativePath;
      if (!uploadedFileName) {
        toast.error("Failed to get uploaded file path");
        setAttachAlertDialogOpen(true);
        return;
      }

      const targetData = completedThreadData || threadData;
      const updatedData = {
        ...targetData,
        thrFile: uploadedFileName
      };

      const result = await manageThreadAPI(updatedData, 'EDIT', masterData?.threadTypes || threadTypes);

      if (result?.error === false && result?.message === 'Success') {
        toast.success("Document attached successfully.");
        setAttachAlertDialogOpen(false);
        setCompletedThreadData(null);
        if (onCloseModal) {
          onCloseModal();
        }
      } else {
        toast.error(result?.message || "Failed to save attachment to sale record.");
        setAttachAlertDialogOpen(true);
      }
    } catch (err) {
      console.error("Error uploading attachment for completed sale:", err);
      toast.error("Failed to upload document.");
      setAttachAlertDialogOpen(true);
    } finally {
      dispatch(setGlobalLoading(false));
      e.target.value = '';
    }
  };

  const handleCancelClick = () => {
    setDialogStates(prev => ({ ...prev, rejectionDialog: true }));
  };

  const handleMarkFake = () => {
    if (isPendingTasks(threadData?.threadID, `Cannot mark inquiry as ${threadData?.currStageID == 1 ? 'fake' : 'rejected'}.`,
      `Please complete tasks before marking this inquiry as  ${threadData?.currStageID == 1 ? 'fake' : 'rejected'}.`)) return;
    setDialogStates(prev => ({ ...prev, rejectionDialog: true }));
  };

  const handleCancelConfirm = (reasonText, isMarkCold = false) => {
    if (threadData.typeID === 1 && threadData.currStageID === 1) {
      onMarkFake(threadData, reasonText, isMarkCold);
    } else {
      onCloseThread(reasonText, isMarkCold);
    }
    setDialogStates(prev => ({ ...prev, rejectionDialog: false }));
  };

  const handleAddReasonClick = (type, searchText = "") => {
    setMastersDialog({
      open: true,
      masterType: type,
      directAdd: true,
      openEntry: true,
      currentThreadTypeID: threadData?.typeID,
      prefilledName: searchText
    });
  };

  const hasVisibleButtons = canPerformActions || canReassign;

  // Desktop View (Original)
  if (!hasVisibleButtons) {
    return (
      <div className="space-y-4">
        {/* <h3 className="text-sm md:text-base font-semibold text-gray-900">
          {isInqOnlyMode ? 'Close Inquiry' : 'Actions'}
        </h3> */}
        <div className={`bg-gray-50 rounded-lg ${!compact ? 'p-3 md:p-4' : ''}`}>
          <p className="text-xs md:text-sm text-gray-500">
            You have no rights to update this {threadTypes.find(tt => tt.TID === threadData?.typeID)?.TypSng || 'thread'}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {/* Order Update Status Button */}
          {isOrder && canPerformActions && onCustomStatusUpdate && (
            <Button
              onClick={onCustomStatusUpdate}
              disabled={!statusText?.trim()}
              className="bg-sahi-dark-blue text-sm px-3.5 py-2 h-auto text-white rounded-[5px] hover:bg-white hover:text-sahi-dark-blue border hover:border-sahi-dark-blue disabled:opacity-50"
            >
              <Check className="h-4 w-4 mr-1" />
              Update Status
            </Button>
          )}

          {/* Complete / Convert Button */}
          {canPerformActions && (
            <Button
              onClick={handleCompleteClick}
              className="bg-sahi-green text-sm px-3.5 py-2 h-auto text-white rounded-[5px] hover:bg-white hover:text-sahi-green border hover:border-sahi-green"
            >
              <Check className="h-4 w-4 mr-1" />
              {getBtnText()}
            </Button>
          )}

          {/* Mark Fake / Rejected (Inquiry only) */}
          {isInquiry && canPerformActions && (
            <Button
              onClick={handleMarkFake}
              className="bg-sahi-red text-sm px-3.5 py-2 h-auto text-white rounded-[5px] hover:bg-white hover:text-sahi-red border hover:border-sahi-red"
            >
              <X className="h-4 w-4 mr-1" />
              {threadData.currStageID > 1 && threadData?.typeID === 1 ? 'Mark Rejected' : 'Mark Fake'}
            </Button>
          )}

          {/* Cancel Button (non-Inquiry) */}
          {!isInquiry && canPerformActions && (
            <Button
              onClick={handleCancelClick}
              className="bg-sahi-red text-sm px-3.5 py-2 h-auto text-white rounded-[5px] hover:bg-white hover:text-sahi-red border hover:border-sahi-red"
            >
              {([2, 5, 3, 6].includes(threadData?.typeID)) ? (
                <Ban className="h-4 w-4 mr-1" />
              ) : (
                <X className="h-4 w-4 mr-1" />
              )}
              {([2, 5].includes(threadData?.typeID)) ? 'Cancel Sale' :
                ([3, 6].includes(threadData?.typeID)) ? 'Cancel Ticket' : 'Cancel'}
            </Button>
          )}

          {/* Close Button */}
          {onCloseModal && (
            <Button
              type="button"
              onClick={onCloseModal}
              className="sahi-secondary-button text-sm px-3.5 py-2 h-auto rounded-[5px] border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              <X className="h-4 w-4 mr-1" />
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <CompleteThreadDialog
        typeID={threadData?.typeID}
        open={dialogStates.confirmDialog}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, confirmDialog: open }))}
        connID={threadData?.connID}
        onComplete={handleCompleteThread}
        showFeedbackOption={true}
      />

      <CancelThreadDialog
        open={dialogStates.rejectionDialog}
        onOpenChange={(open) => setDialogStates(prev => ({ ...prev, rejectionDialog: open }))}
        onConfirm={handleCancelConfirm}
        typeID={threadData?.typeID}
        currStageID={threadData?.currStageID}
        rejectionReasonOptions={rejectionReasonOptions}
        disqlRsns={disqlRsns}
        requireReason={true}
        onAddReasonClick={handleAddReasonClick}
        connID={threadData?.connID}
      />

      <MastersDialog
        open={mastersDialog.open}
        onClose={() => setMastersDialog({
          open: false,
          masterType: '',
          directAdd: false,
          openEntry: false,
          showThreadTypeSelection: false,
          showThreadTypeSelection: false,
          currentThreadTypeID: null,
          prefilledName: ''
        })}
        masterType={mastersDialog.masterType}
        parentDialogOpen={dialogStates.rejectionDialog}
        directAdd={mastersDialog.directAdd}
        openEntry={mastersDialog.openEntry}
        prefilledName={mastersDialog.prefilledName}
        showThreadTypeSelection={mastersDialog.showThreadTypeSelection}
        preselectedThreadTypeID={mastersDialog.currentThreadTypeID}
        onProductAdded={(itemName) => {
          setMastersDialog({
            open: false,
            masterType: '',
            directAdd: false,
            openEntry: false,
            showThreadTypeSelection: false,
            currentThreadTypeID: null,
            prefilledName: ''
          });
        }}
      />

      {/* Hidden file picker for document upload after complete sale */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.csv"
        onChange={handleFileChange}
      />

      {/* Confirmation AlertDialog for Attaching Document */}
      <AlertDialog
        open={attachAlertDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleSkipAttachment();
          }
        }}
      >
        <AlertDialogContent className="w-[92%] sm:w-full max-w-md rounded-2xl p-4 bg-white gap-4 border border-zinc-200/80 shadow-2xl">
          <AlertDialogHeader className="text-left gap-1.5">
            <AlertDialogTitle className="text-base sm:text-lg font-semibold text-zinc-900">
              Attach Document / Invoice
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-zinc-600 leading-normal">
              Do you want to attach a document or invoice for this sale?
              {/* <span className="font-semibold text-zinc-900">
                "{threadData?.connectionData?.bizName || threadData?.connectionData?.contactName || threadData?.contactPerson || threadData?.mainProduct || (threadData?.reference ? `Sale #${threadData.reference}` : "this sale")}"
              </span> */}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="!flex !flex-row sm:!justify-start gap-2.5 pt-1">
            <Button
              type="button"
              onClick={handleAttachDocumentClick}
              className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[100px] h-10"
            >
              <Check className="w-3.5 h-3.5 stroke-[2.5]" /> Accept
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleSkipAttachment}
              className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px] h-10"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ThrDetailsActions;